import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Avatar, Space, Dropdown } from 'antd';
import { routeHistory } from '@nackle/biw-web-router';
import { UserOutlined } from '@ant-design/icons';
import { CompanySelect } from '@nackle/studio-tools';
import auth from '../../../auth/auth';
import { canShowBIWAdminStuff } from '../../../auth/auth';
import { useAuthState } from '../../../auth/useAuthState';
import { authService } from '../../../auth/authService';
import { apolloService } from '../../../services';
import { COMPANY_UUID_REGEX, Routes } from '../../../routes';
import './styles.less';

const AppHeader = () => {
    const { claims: { family_name, given_name } = {} } = useAuthState();
    const [ showCompanyChooser ] = useState( canShowBIWAdminStuff() );
    const [ companyUUID, setCompanyUUID ] = useState( '' );
   
    useEffect( () => {
        if ( showCompanyChooser && companyUUID === '' ) {
            setCompanyUUID( authService.getCompanyId() );
        } 
    }, [ showCompanyChooser, companyUUID ] ) ;

    const handleSelectCompany = ( key ) => {
        // Clear Apollo's cache so that none of the previous company's data exists. Can use
        // `apolloService.resetStore()` to clear the store and refetch active queries.
        apolloService.clearStore();

        // Add the selected company UUID to the URL.
        routeHistory.push( '/' + key );
    }; 

    const handleRouteChanged = useCallback( ( action, nextRoute, prevRoute ) => {
        const match = nextRoute.pathname.match( COMPANY_UUID_REGEX );
        
        if ( match ) { 
            const id =  match[ 0 ].substring( 1 );

            if ( id !== companyUUID ) {
                // Keep the chooser's selected value in sync. The actual company-data
                // (re)load is owned by CompanyProvider, which reacts to this same route
                // change — so we intentionally do NOT fetch here (that raced delegation
                // and briefly loaded the home company's menu).
                setCompanyUUID( id );
            }
        } else { 
            setCompanyUUID( '' );
        }
    }, [ companyUUID, setCompanyUUID ] );

    useEffect( () => { 
        routeHistory.addEventListener( 'changed', handleRouteChanged );
        
        return () => {
            routeHistory.removeEventListener( 'changed', handleRouteChanged );
        };
    }, [ handleRouteChanged ] );

    const displayName = useMemo( () => {
        return [ given_name, family_name ].filter( token => token ).join( ' ' );
    }, [ given_name, family_name ] );

    const handleMenuClick = ( item ) => {
        if ( item.key === 'settings' ) {
            routeHistory.push( Routes.Settings.buildPath() );
        } else {
            if ( item.key === 'logout' ) {
                authService.signOut();
            }
        }
    };
   
    return (
        <div className="app-header">
            <div className="app-header-content">
                { ( showCompanyChooser && companyUUID ) &&
                    <CompanySelect
                        auth={ auth }
                        companyId={ companyUUID }    
                        onChange={ ( company ) => handleSelectCompany( company.id ) }
                    />
                }
            </div>
            
            <div className="app-header-user">
                <Dropdown
                    menu={ {
                        items: [
                            {
                                label: 'Settings',
                                key: 'settings'
                            },
                            {
                                label: 'Logout',
                                key: 'logout',
                            } 
                        ],
                        onClick: handleMenuClick
                    } }
                >
                    <div className="avatar-trigger" role="button" tabIndex={ 0 }>
                        <Space>
                            { displayName &&
                                <span>{ displayName }</span>
                            }
                            <Avatar icon={ <UserOutlined /> }/>
                        </Space>
                    </div>
                </Dropdown>
            </div>
        </div>
    );
};

export default AppHeader;
